import React, { useState } from 'react';

// Face-evidence skin-read card (approved plan 2026-07-13). The read lives ON the
// user's scan photo: soft highlight pins at the named face zone for each finding,
// tap a pin ↔ its cue. Qualitative BANDS + a specific cue per dimension — no
// numbers, no radar. Editorial: lead with the 1-2 standouts, quiet the rest.

const SERIF = 'var(--font-sonmi-display)';
const SANS = "'DM Sans', system-ui, sans-serif";

// Band → warm health colours (plan: terracotta → amber → sage → deep sage).
const BAND = {
  'Needs care': { dot: '#C58A72', bg: '#F4E4DB', fg: '#9A5A42' },
  'Fair':       { dot: '#D0A968', bg: '#F7EEDB', fg: '#8A6A2E' },
  'Good':       { dot: '#8DAE92', bg: '#E9F0EA', fg: '#4A6B54' },
  'Great':      { dot: '#6B9478', bg: '#DDEAE0', fg: '#385A44' },
};
const bandOf = (b) => BAND[b] || BAND['Good'];

// Named zones → approximate positions on a centered, sized front-face portrait
// (the guided capture frames the face, so a fixed template is close enough).
// A fixed template — deliberately not pixel coordinates.
const ZONE_POS = {
  'forehead':   { x: 50, y: 21 },
  't-zone':     { x: 50, y: 35 },
  'nose':       { x: 50, y: 47 },
  'left-cheek': { x: 34, y: 51 },
  'right-cheek':{ x: 66, y: 51 },
  'cheeks':     { x: 50, y: 53 },
  'chin':       { x: 50, y: 75 },
  'jaw':        { x: 31, y: 67 },
  'under-eyes': { x: 50, y: 41 },
  'overall':    null,
};

function FacePlaceholder() {
  // A calm neutral face outline so the pins have context in the mock; the real
  // scan photo replaces this.
  return (
    <svg viewBox="0 0 300 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="300" height="380" fill="#EFE2D4" />
      <ellipse cx="150" cy="168" rx="78" ry="94" fill="#E7D3BE" />
      <path d="M150 150 Q145 176 150 186 Q156 186 158 182" fill="none" stroke="#CBB49B" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M112 132 Q126 126 140 132 M160 132 Q174 126 188 132" fill="none" stroke="#C3AB8F" strokeWidth="3" strokeLinecap="round" />
      <path d="M120 210 Q150 224 180 210" fill="none" stroke="#CBB49B" strokeWidth="3" strokeLinecap="round" />
      <path d="M150 262 Q120 300 122 380 M150 262 Q180 300 178 380" fill="none" stroke="#DCC8B2" strokeWidth="2" />
    </svg>
  );
}

export default function SkinReadCard({ read, imageUrl, isPlaceholderPhoto = false }) {
  const dims = read?.dimensions || [];
  const [active, setActive] = useState(null); // dimension key

  // Standouts: the model can tag kind, else derive (best band + worst band).
  const order = ['Needs care', 'Fair', 'Good', 'Great'];
  const rank = (d) => order.indexOf(d.band);
  const watch = read?.standoutWatch || [...dims].filter(d => d.band !== 'overall').sort((a, b) => rank(a) - rank(b))[0]?.key;
  const strength = read?.standoutStrength || [...dims].sort((a, b) => rank(b) - rank(a))[0]?.key;
  const standoutKeys = [strength, watch].filter(Boolean);
  const standouts = standoutKeys.map(k => dims.find(d => d.key === k)).filter(Boolean);
  const rest = dims.filter(d => !standoutKeys.includes(d.key));

  const pins = dims.filter(d => d.zone && d.zone !== 'overall' && ZONE_POS[d.zone]);

  return (
    <div style={{ fontFamily: SANS, width: '100%', maxWidth: 380, background: '#FBF6F0', border: '1px solid #EAE0D6', borderRadius: 22, overflow: 'hidden' }}>
      {/* Hero: photo + evidence pins */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 3.4', background: '#EFE2D4' }}>
        {imageUrl ? (
          <img src={imageUrl} alt="Your scan" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <FacePlaceholder />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(42,26,20,.38), rgba(42,26,20,0) 34%)' }} />
        <span style={{ position: 'absolute', top: 12, left: 14, fontSize: 13, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,.94)' }}>Skin read</span>
        <span style={{ position: 'absolute', top: 11, right: 12, background: 'rgba(255,255,255,.9)', color: '#7A6A5B', fontSize: 13, fontWeight: 500, padding: '3px 9px', borderRadius: 9 }}>
          {read?.confidence === 'soft' ? 'Soft light' : 'Clear read'}
        </span>

        {pins.map((d) => {
          const pos = ZONE_POS[d.zone];
          const c = bandOf(d.band);
          const on = active === d.key;
          return (
            <button key={d.key} type="button" onClick={() => setActive(on ? null : d.key)}
              style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, transform: 'translate(-50%,-50%)', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', zIndex: on ? 3 : 2 }}
              aria-label={`${d.name}: ${d.band}`}>
              <span style={{ display: 'block', width: on ? 18 : 13, height: on ? 18 : 13, borderRadius: '50%', background: c.dot, boxShadow: `0 0 0 ${on ? 4 : 3}px rgba(255,255,255,.9), 0 1px 3px rgba(0,0,0,.25)`, transition: 'all .15s' }} />
              {on && (
                <span style={{ position: 'absolute', top: 22, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', background: '#2C211B', color: '#FBF6F0', fontSize: 13, fontWeight: 500, padding: '3px 8px', borderRadius: 7 }}>
                  {d.name} · {d.band}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ padding: '15px 17px 17px' }}>
        {isPlaceholderPhoto && (
          <p style={{ margin: '0 0 10px', fontSize: 13, color: '#B0A091', fontStyle: 'italic' }}>Placeholder photo — your actual scan fills this.</p>
        )}
        {read?.headline && (
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 500, fontSize: 20, lineHeight: 1.25, color: '#2C211B', margin: '0 0 12px' }}>{read.headline}</p>
        )}

        {/* Classification (captured, not scored) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          {read?.toneHex && <span style={{ width: 20, height: 20, borderRadius: '50%', background: read.toneHex, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)', flex: 'none' }} />}
          <span style={{ fontSize: 14.5, fontWeight: 500, color: '#2C211B' }}>
            {read?.tone}
            <span style={{ color: '#93826F', fontWeight: 400 }}>
              {read?.undertone ? ` · ${read.undertone} undertone` : ''}{read?.skinType ? ` · ${read.skinType.toLowerCase()}` : ''}
            </span>
          </span>
        </div>

        <div style={{ height: 1, background: '#F1EAE1', margin: '15px 0' }} />

        {/* What stood out */}
        <div style={{ fontSize: 13, letterSpacing: '.11em', textTransform: 'uppercase', color: '#A28E7E', marginBottom: 12 }}>What stood out</div>
        {standouts.map((d) => {
          const c = bandOf(d.band);
          return (
            <button key={d.key} type="button" onClick={() => setActive(active === d.key ? null : d.key)}
              style={{ display: 'flex', gap: 11, width: '100%', textAlign: 'left', background: active === d.key ? '#F6EFE6' : 'transparent', border: 'none', borderRadius: 12, padding: '9px 8px', marginBottom: 4, cursor: 'pointer' }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.dot, flex: 'none', marginTop: 6 }} />
              <span style={{ flex: 1 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#2C211B' }}>{d.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, padding: '2px 9px', borderRadius: 9, background: c.bg, color: c.fg }}>{d.band}</span>
                </span>
                <span style={{ display: 'block', marginTop: 4, fontSize: 13.5, color: '#8A7A6C', lineHeight: 1.5 }}>{d.cue}</span>
              </span>
            </button>
          );
        })}

        {/* Full read */}
        <div style={{ fontSize: 13, letterSpacing: '.11em', textTransform: 'uppercase', color: '#A28E7E', margin: '16px 0 10px' }}>Full read</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rest.map((d) => {
            const c = bandOf(d.band);
            const on = active === d.key;
            return (
              <div key={d.key}>
                <button type="button" onClick={() => setActive(on ? null : d.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flex: 'none' }} />
                  <span style={{ flex: 1, fontSize: 13.5, color: '#2C211B' }}>{d.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, padding: '2px 9px', borderRadius: 9, background: c.bg, color: c.fg }}>{d.band}</span>
                </button>
                {on && <p style={{ margin: '0 8px 6px 26px', fontSize: 13.5, color: '#8A7A6C', lineHeight: 1.5 }}>{d.cue}</p>}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 7, marginTop: 15, paddingTop: 12, borderTop: '1px solid #F1EAE1' }}>
          <span style={{ fontSize: 13, color: '#C3B4A4', flex: 'none', marginTop: 1 }} aria-hidden="true">ⓘ</span>
          <p style={{ margin: 0, fontSize: 13, color: '#A9998A', lineHeight: 1.45 }}>A read of what your photo shows — appearance, not a diagnosis. For anything that concerns you, see a professional.</p>
        </div>
      </div>
    </div>
  );
}
